import { z } from 'zod';
import { idSchema, instantSchema } from './common.js';
/**
 * Project functions ("responsible roles"): a per-organisation list (Account Manager, Sales Rep,
 * Recruiter…) an admin edits in Settings → Projects. Each project holds at most one person per
 * function, beside its owner. NOT the RBAC roles: a function grants nothing by itself, but whoever
 * holds one on a project counts as an owner of it for `owned` and `team` scope (and of its deals),
 * is matched by the Projects list's "Mine", and can be the assignee of an automation.
 */
export const PROJECT_ROLE_NAME_MAX = 80;
/**
 * A function's slug: its readable, permanent key (`account-manager`) for integrations, taken
 * from the name when it is created (made unique in the organisation with `-2`, `-3`…) and never
 * changed by a rename nor reused after a removal — the same rule as a stage's slug.
 */
export const projectRoleSlugSchema = z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lower-case words joined by hyphens');
export const projectRoleSchema = z.object({
    id: idSchema,
    name: z.string(),
    /** Permanent readable key (`account-manager`); see `projectRoleSlugSchema`. */
    slug: z.string(),
    position: z.number().int(),
    /** Live projects that have someone in this function. */
    assignedCount: z.number().int(),
    createdAt: instantSchema,
    updatedAt: instantSchema,
});
export const projectRoleInputSchema = z.object({
    name: z.string().trim().min(1).max(PROJECT_ROLE_NAME_MAX),
});
/** Every live function exactly once, in the new order. */
export const projectRoleOrderSchema = z.object({
    ids: z
        .array(idSchema)
        .min(1)
        .refine((ids) => new Set(ids).size === ids.length, { message: 'a function is listed twice' }),
});
/** Who holds one function on a project. */
export const projectResponsibleSchema = z.object({
    roleId: idSchema,
    roleName: z.string(),
    roleSlug: z.string(),
    userId: idSchema,
});
/**
 * `PUT /projects/{id}/responsibles/{role}` (`role`: the function's id or slug): the person in
 * that function, or null to clear it.
 */
export const projectResponsibleSetSchema = z.object({ userId: idSchema.nullable() });
//# sourceMappingURL=project-roles.js.map